// Static configuration + runtime env resolution for the retells auth worker
// (docs/plan/08-auth-worker.md §7). The worker holds ZERO secrets: the broker
// `/auth/exchange` takes no client credential (the one-time code is the bearer)
// and JWKS verification uses public keys. Every value here is plain-text and
// safe to commit / expose. `wrangler.jsonc` `vars` override the defaults so a
// PD-1 issuer-scheme change (§8) is a one-line config edit, not a code change.

/** Cloudflare `vars` bound to the worker. All optional — defaults below win. */
export interface Env {
  AUTH_BROKER_ORIGIN?: string;
  AUTH_EXPECTED_ISSUER?: string;
  AUTH_EXPECTED_AUDIENCE?: string;
  AUTH_EXCHANGE_AUDIENCE?: string;
  // Logging proxy (L3, docs/plan/09-logging.md). `AXIOM_TOKEN` is the worker's
  // ONE secret (`wrangler secret put`); `AXIOM_DATASET` is a plain `vars` value.
  // Both absent in dev — `/api/log` then no-ops (204) instead of failing.
  AXIOM_TOKEN?: string;
  AXIOM_DATASET?: string;
}

export interface AuthConfig {
  /** Broker origin — exchange + sign-out + JWKS base. */
  brokerOrigin: string;
  /** Expected `iss` on the broker JWT (bare `auth.sara.ai` — tracks PD-1). */
  expectedIssuer: string;
  /** Expected `aud` on the broker JWT (broker `DEFAULT_AUDIENCE`). */
  expectedAudience: string;
  /**
   * Optional override for the hostname binding presented to `/auth/exchange`.
   * Normally UNSET: with mirrors the audience is the request host, because the
   * code was minted against whichever host started sign-in. Set it only to pin
   * one audience (e.g. dev, where the request host is `localhost` and the
   * broker won't mint codes for it).
   */
  exchangeAudiencePin?: string;
}

const DEFAULTS = {
  brokerOrigin: "https://auth.sara.ai",
  expectedIssuer: "auth.sara.ai",
  expectedAudience: "sara.ai",
} as const;

/** Resolve the effective config from the worker env, falling back to defaults. */
export function resolveConfig(env: Env): AuthConfig {
  return {
    brokerOrigin: env.AUTH_BROKER_ORIGIN ?? DEFAULTS.brokerOrigin,
    expectedIssuer: env.AUTH_EXPECTED_ISSUER ?? DEFAULTS.expectedIssuer,
    expectedAudience: env.AUTH_EXPECTED_AUDIENCE ?? DEFAULTS.expectedAudience,
    exchangeAudiencePin: env.AUTH_EXCHANGE_AUDIENCE,
  };
}

/**
 * The audience to claim the one-time code with. Guardrail 2 still holds: this
 * is NOT the raw request `Host`. The caller must have already gated `hostname`
 * through `isAuthHost`, so a spoofed or misrouted host can only ever yield an
 * audience from the fixed `AUTH_HOSTS` allowlist — never an arbitrary one.
 */
export function exchangeAudienceFor(
  config: AuthConfig,
  hostname: string,
): string {
  return config.exchangeAudiencePin ?? hostname;
}

/**
 * Hosts retells serves the auth flow on. `retells.sara.ai` and
 * `retells.read-by-ear.com` are peer MIRRORS, not primary-and-alias: sign-in
 * completes on whichever host started it, and each keeps its OWN session —
 * `cookies.ts` sets no `Domain`, so the cookies are host-only and never shared.
 * The three `fugl.dev` hosts are the STAGING peers under the same rule: real
 * hosts we own, one per wrangler environment (`wrangler.jsonc`), deployed to
 * ahead of every critical cutover — so sign-in has to complete on them rather
 * than bounce off. The dev peer (`localhost`) lives in `DEV_AUTH_HOSTS`
 * below, admitted only over plain HTTP.
 *
 * Every entry needs a route attached in `wrangler.jsonc` AND broker-side
 * allowlisting (redirect target + exchange audience). A host named here but not
 * actually served dead-ends sign-in — which is exactly what happened while
 * `retells.sara.ai` was the sole canonical host with its route commented out.
 * The reverse drift dead-ends it too, and is what a staging deploy hits first:
 * routed but unnamed, `isAuthHost` is false and `auth.ts` sends the callback to
 * `DEFAULT_ORIGIN` — off the host the user started on, onto a prod mirror.
 *
 * The two `sara.ai`/`read-by-ear.com` mirrors are named here but routed by
 * NOTHING in this repo: they belong to the `retells` worker deployed from
 * ../retells. They stay listed so the allowlist is already correct on the day
 * this repo claims them; until then they are unreachable here, not broken.
 */
export const AUTH_HOSTS = [
  "retells.sara.ai",
  "retells.read-by-ear.com",
  "retells.fugl.dev",
  "retells-preview.fugl.dev",
  "retells-dev.fugl.dev",
] as const;

/**
 * The dev peer. Separated from `AUTH_HOSTS` and admitted only over plain HTTP,
 * because `localhost` is a name an ATTACKER can put in a `Host` header, not one
 * we own: sitting unconditionally in the deployed allowlist meant a request
 * claiming `Host: localhost` would be treated as a session-holding auth host,
 * minting `localhost` as its own exchange audience. No reachable path was found
 * (Cloudflare dispatches `custom_domain` routes by hostname, so such a request
 * never arrives), so this is posture, not a closed hole — but the allowlist is
 * guardrail 2's default-deny, and an entry that can only ever be legitimate in
 * dev should say so in code rather than in a comment.
 */
export const DEV_AUTH_HOSTS = ["localhost"] as const;

/**
 * Whether retells serves auth (and may hold a session cookie) on `hostname`.
 * `secure` is the handler's `url.protocol === "https:"` flag: over HTTPS only
 * the real hosts qualify, so the dev peer cannot be asserted against the
 * deployed worker (which is HTTPS-only in every route it is attached to).
 */
export function isAuthHost(hostname: string, secure: boolean): boolean {
  if ((AUTH_HOSTS as readonly string[]).includes(hostname)) return true;
  return !secure && (DEV_AUTH_HOSTS as readonly string[]).includes(hostname);
}

/**
 * Where traffic arriving on an UNKNOWN host is funnelled (a `*.workers.dev`
 * preview, a future alias). Not a canonical host — the mirrors are peers — just
 * the defined landing spot for a host we don't serve auth on.
 */
export const DEFAULT_HOST = "retells.read-by-ear.com";
export const DEFAULT_ORIGIN = `https://${DEFAULT_HOST}`;

/**
 * Session cookie name. Deliberately NOT the broker's `sara-auth-session`:
 * cookies ignore ports, so on `localhost` dev the broker and retells would
 * clobber each other's host-only cookie under a shared name (plan §3).
 */
export const SESSION_COOKIE = "retells-session";
/** Short-TTL circuit-breaker attempt counter (guardrail 7). */
export const TRY_COOKIE = "retells-authtry";

/**
 * Sign-in CSRF nonce. `signIn` mints it, stores it here, and round-trips the
 * same value through the broker as a `state` query param on the callback URL;
 * `/auth/callback` refuses to exchange a code whose `state` doesn't match.
 *
 * Deliberately NOT `HttpOnly`, unlike every other cookie here: `signIn` runs in
 * the page and must be able to overwrite it, and a browser silently ignores a
 * `document.cookie` write that would replace an `HttpOnly` cookie of the same
 * name — so an `HttpOnly` nonce would wedge every subsequent sign-in for its
 * whole TTL. It is not a credential: it may be read by same-origin JS (which
 * could mint its own anyway) and never by a cross-origin page, which is exactly
 * the property the check depends on.
 */
export const STATE_COOKIE = "retells-authstate";
/**
 * Nonce lifetime. The broker's own signed-state TTL is 10 minutes, so a sign-in
 * that outlived this could not have completed there either — a longer window
 * would only widen the replay surface for no reachable benefit.
 */
export const STATE_TTL_SECONDS = 600;
/** Entropy in the nonce. 16 bytes → 22 base64url chars. */
export const STATE_BYTES = 16;

/** Cookies are scoped to `/auth` — the JWT never rides static-asset requests. */
export const COOKIE_PATH = "/auth";

export const MAX_TRIES = 5;
export const TRY_TTL_SECONDS = 120;
/** Never let the cookie outlive the token it stores (broker default 7d). */
export const MAX_SESSION_SECONDS = 7 * 24 * 60 * 60;
